/**
 * Intent signal for "navigate back to the home/search page" (top-bar logo or
 * bottom-nav "Search").
 *
 * Navigating to the search page fires `FLOW_CHANGE → HomePageFlow` (which
 * restarts the booking flow synchronously) and `ROUTE_CHANGE → /search` (an
 * async Next.js navigation). The restart therefore runs while
 * `window.location.pathname` can still read `/train-offer`, which would make
 * `MainBookingInit` take its hard-refresh branch and mount the offer flows
 * instead of the `SearchFlow` — leaving the search bar's Search button with no
 * event subscriber. Setting this flag tells `MainBookingInit` to mount the
 * `SearchFlow` regardless of the mid-transition pathname.
 *
 * The flag is consumed by the next `MainBookingInit.onExecute()` — microtasks
 * later in the same event-loop turn, since `EventBus` dispatches handlers via
 * `queueMicrotask`. It is timestamped and expires so a leftover flag (e.g. a
 * restart that errored before reaching `MainBookingInit`) cannot wrongly force
 * the `SearchFlow` on a later genuine `/train-offer` reload.
 */
const SEARCH_FLOW_NAVIGATION_KEY = "forceSearchFlowNavigation";

/** Max age for the intent flag; the restart consumes it microtasks later, far inside this window. */
const SEARCH_FLOW_NAVIGATION_TTL_MS = 5000;

const hasSessionStorage = (): boolean =>
  typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

/** Mark that the next HomePageFlow restart should land on the SearchFlow. */
export const flagSearchFlowNavigation = (): void => {
  if (!hasSessionStorage()) return;
  sessionStorage.setItem(SEARCH_FLOW_NAVIGATION_KEY, Date.now().toString());
};

/**
 * Read-and-clear the navigation intent. Returns true only when the flag was set
 * within {@link SEARCH_FLOW_NAVIGATION_TTL_MS}, so a stale leftover flag is
 * ignored and the caller falls back to its pathname-based behaviour.
 */
export const consumeSearchFlowNavigation = (): boolean => {
  if (!hasSessionStorage()) return false;
  const raw = sessionStorage.getItem(SEARCH_FLOW_NAVIGATION_KEY);
  sessionStorage.removeItem(SEARCH_FLOW_NAVIGATION_KEY);
  if (!raw) return false;
  const stampedAt = Number(raw);
  return Number.isFinite(stampedAt) && Date.now() - stampedAt < SEARCH_FLOW_NAVIGATION_TTL_MS;
};
